import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';

import { useUpload } from '@/components/asset-manager/hooks/useUpload';
import { Button } from '@/components/ui/button';
import { Heading4, MutedText, SmallText } from '@/components/ui/typography';
import type { AssetFileTypes } from '@/lib/assets-config';
import { getAcceptAttribute, getSupportedFileTypesText } from '@/lib/assets-config';
import { cn } from '@/lib/utils';
import { UploadProgressPanel } from './upload-progress-panel';

export interface SelectFilesPanelProps {
  onFilesSelected: (files: File[]) => void;
  onClose: () => void;
  fileTypes?: AssetFileTypes;
}

export interface UploadFilesPanelProps {
  onUploadComplete: () => void;
  fileTypes?: AssetFileTypes;
  folderId?: string | null;
  resolveFolderId?: () => string | null | undefined;
  className?: string;
}

interface AssetFileDropzoneProps {
  fileTypes: AssetFileTypes;
  className?: string;
  onDrop: (files: File[]) => void;
}

function AssetFileDropzone({ fileTypes, className, onDrop }: AssetFileDropzoneProps) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: getAcceptAttribute(fileTypes),
    onDropRejected: (rejections) => {
      for (const { file } of rejections) {
        toast.error(`Unsupported file type: ${file.name}`);
      }
    },
  });

  return (
    <div
      {...getRootProps()}
      className={cn(
        'flex h-[350px] cursor-pointer flex-col items-center justify-center rounded-[3px] border border-dashed',
        isDragActive ? 'border-primary bg-primary/5' : 'border-primary/40 bg-primary/5',
        className,
      )}
    >
      <input {...getInputProps()} />
      <div className="space-y-4 px-8 text-center">
        <Heading4>Drag & drop files here</Heading4>
        <MutedText>or</MutedText>
        <Button>Upload from Computer</Button>
        <SmallText className="text-muted-foreground text-balance leading-tight">
          File types supported - {getSupportedFileTypesText(fileTypes)}
        </SmallText>
      </div>
    </div>
  );
}

export function SelectFilesPanel({
  onFilesSelected,
  onClose,
  fileTypes = 'all',
}: SelectFilesPanelProps) {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      onFilesSelected(acceptedFiles);
      onClose();
    },
    [onFilesSelected, onClose],
  );

  return <AssetFileDropzone fileTypes={fileTypes} onDrop={onDrop} />;
}

export function UploadFilesPanel({
  onUploadComplete,
  fileTypes = 'all',
  folderId,
  resolveFolderId,
  className,
}: UploadFilesPanelProps) {
  const { startUploads, uploadingFiles, cancelUpload, dismiss } = useUpload({
    onAllComplete: onUploadComplete,
  });

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      const targetFolderId = resolveFolderId?.() ?? folderId;
      startUploads(acceptedFiles, targetFolderId);
    },
    [startUploads, folderId, resolveFolderId],
  );

  return (
    <>
      <AssetFileDropzone fileTypes={fileTypes} className={className} onDrop={onDrop} />
      <UploadProgressPanel
        uploadingFiles={uploadingFiles}
        onDismiss={dismiss}
        onCancelUpload={cancelUpload}
      />
    </>
  );
}
